/**
 * OCTOPUS Dashboard - Widget Rudder (Leme)
 * Barra simétrica com zero central
 * 
 * Range: -40° a +40°
 * Cores:
 *   - Verde central: -1° a +1° (leme alinhado)
 *   - Verde: valores positivos (estibordo/boreste)
 *   - Vermelho: valores negativos (bombordo)
 *   - Laranja: extremidades (últimos 5°)
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React, { useMemo } from 'react';

const GaugeRudder = ({
  value = 0,
  min = -40,
  max = 40,
  unit = '°',
  label = 'Leme',
  showLabel = true,
  showUnit = true,
  width = 280,
  height = 80,
  centerZone = 1,      // Graus para zona verde central (cada lado)
  extremeZone = 5,     // Graus para zona laranja nas extremidades
}) => {
  // Calcular posição do indicador (0-100%, onde 50% é o centro)
  const indicatorPosition = useMemo(() => {
    const clampedValue = Math.max(min, Math.min(max, value));
    // Mapear de [min, max] para [0, 100]
    return ((clampedValue - min) / (max - min)) * 100;
  }, [value, min, max]);

  // Determinar a cor baseada no valor
  const indicatorColor = useMemo(() => {
    const absValue = Math.abs(value);
    
    // Zona central verde (leme alinhado)
    if (absValue <= centerZone) {
      return '#22c55e'; // green-500
    }
    
    // Zona laranja (extremidades)
    if (absValue >= (max - extremeZone)) {
      return '#f97316'; // orange-500
    }
    
    // Estibordo (positivo) = verde
    if (value > 0) {
      return '#22c55e'; // green-500
    }
    
    // Bombordo (negativo) = vermelho
    return '#ef4444'; // red-500
  }, [value, centerZone, extremeZone, max]);

  // Formatar valor para exibição
  const formattedValue = useMemo(() => {
    if (typeof value !== 'number' || isNaN(value)) return '--';
    const absValue = Math.abs(value).toFixed(1);
    if (value > centerZone) return `${absValue}° BE`; // Boreste/Estibordo
    if (value < -centerZone) return `${absValue}° BB`; // Bombordo
    return `${absValue}°`;
  }, [value, centerZone]);

  // Zonas de fundo
  const backgroundZones = useMemo(() => {
    const totalRange = max - min;
    const centerPercent = 50;
    const centerZonePercent = (centerZone / (totalRange / 2)) * 50;
    const extremeZonePercent = (extremeZone / (totalRange / 2)) * 50;
    
    return [
      // Laranja extremo esquerdo (bombordo extremo)
      { start: 0, end: extremeZonePercent, color: '#f97316', opacity: 0.2 },
      // Vermelho bombordo
      { start: extremeZonePercent, end: centerPercent - centerZonePercent, color: '#ef4444', opacity: 0.15 },
      // Verde central
      { start: centerPercent - centerZonePercent, end: centerPercent + centerZonePercent, color: '#22c55e', opacity: 0.25 },
      // Verde estibordo
      { start: centerPercent + centerZonePercent, end: 100 - extremeZonePercent, color: '#22c55e', opacity: 0.15 },
      // Laranja extremo direito (estibordo extremo)
      { start: 100 - extremeZonePercent, end: 100, color: '#f97316', opacity: 0.2 },
    ];
  }, [min, max, centerZone, extremeZone]);

  // Marcações de escala
  const scaleMarks = useMemo(() => {
    const marks = [];
    const step = 10;
    for (let v = min; v <= max; v += step) {
      const position = ((v - min) / (max - min)) * 100;
      marks.push({ value: v, position });
    }
    return marks;
  }, [min, max]);

  return (
    <div 
      className="flex flex-col gap-2 p-3 bg-gray-900 rounded-lg border border-gray-700"
      style={{ width, minHeight: height }}
      data-testid="gauge-rudder"
    >
      {/* Header: Label + Valor */}
      <div className="flex items-center justify-between">
        {showLabel && label && (
          <span className="text-gray-300 font-medium text-sm">{label}</span>
        )}
        <div className="flex items-baseline gap-1">
          <span 
            className="font-mono font-bold text-lg"
            style={{ 
              color: indicatorColor,
              textShadow: `0 0 8px ${indicatorColor}40`,
            }}
          >
            {formattedValue}
          </span>
        </div>
      </div>

      {/* Indicadores de lado */}
      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span>BB</span>
        <span>BE</span>
      </div>

      {/* Barra principal */}
      <div className="relative h-8 bg-gray-800 rounded overflow-hidden">
        {/* Zonas de fundo */}
        {backgroundZones.map((zone, index) => (
          <div
            key={index}
            className="absolute top-0 h-full"
            style={{
              left: `${zone.start}%`,
              width: `${zone.end - zone.start}%`,
              backgroundColor: zone.color,
              opacity: zone.opacity,
            }}
          />
        ))}

        {/* Linha central (zero) */}
        <div
          className="absolute top-0 h-full w-0.5 bg-gray-400 z-10"
          style={{ left: '50%', transform: 'translateX(-50%)' }}
        />

        {/* Marcações de escala */}
        {scaleMarks.map((mark) => (
          <div
            key={mark.value}
            className="absolute top-0 h-full"
            style={{ left: `${mark.position}%` }}
          >
            <div 
              className={`absolute bottom-0 w-px ${mark.value === 0 ? 'h-full bg-gray-400' : 'h-2 bg-gray-600'}`}
              style={{ transform: 'translateX(-50%)' }}
            />
          </div>
        ))}

        {/* Barra de valor (do centro até a posição atual) */}
        <div
          className="absolute top-1 bottom-1 rounded transition-all duration-200"
          style={{
            left: value >= 0 ? '50%' : `${indicatorPosition}%`,
            width: `${Math.abs(indicatorPosition - 50)}%`,
            backgroundColor: indicatorColor,
            boxShadow: `0 0 12px ${indicatorColor}60`,
          }}
        />

        {/* Indicador de posição atual (triângulo/ponteiro) */}
        <div
          className="absolute top-0 h-full flex items-center justify-center transition-all duration-200 z-20"
          style={{ left: `${indicatorPosition}%`, transform: 'translateX(-50%)' }}
        >
          <div 
            className="w-1 h-full rounded"
            style={{ 
              backgroundColor: '#ffffff',
              boxShadow: '0 0 6px rgba(255,255,255,0.8)',
            }}
          />
        </div>
      </div>

      {/* Escala numérica */}
      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span>{min}°</span>
        <span>0°</span>
        <span>+{max}°</span>
      </div>
    </div>
  );
};

export default GaugeRudder;
